import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Trash2, Loader2 } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().split("T")[0];
const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const emptyPayment = (balance) => ({
  amount: balance > 0 ? Number(balance.toFixed(2)) : "",
  payment_date: today(),
  payment_mode: "bank_transfer",
  utr_number: "",
  bank_account_id: "",
  tds_applicable: false,
  tds_rate: 2,
  notes: "",
});

export const InvoicePaymentDialog = ({ invoice, open, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyPayment(0));
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState("");

  const total = invoice?.total_amount || 0;
  const paid = invoice?.amount_paid || 0;
  const balance = Math.max(total - paid, 0);
  const canPay = invoice && !["paid", "cancelled"].includes(invoice.status);

  useEffect(() => {
    if (!open || !invoice) return;
    setForm(emptyPayment(balance));
    axios.get(`${API_URL}/bank-accounts`).then((r) => setBankAccounts(r.data.filter((b) => b.is_active !== false))).catch(() => {});
  }, [open, invoice?.id, balance]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    if (amount > balance + 0.01) return toast.error(`Amount exceeds balance due of ${fmt(balance)}`);
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/invoices/${invoice.id}/payments`, {
        ...form,
        amount,
        utr_number: form.utr_number || null,
        bank_account_id: form.bank_account_id || null,
        tds_rate: form.tds_applicable ? parseFloat(form.tds_rate) : null,
        notes: form.notes || null,
      });
      toast.success(data.status === "paid" ? "Invoice fully paid" : `Payment recorded — balance ${fmt(data.total_amount - data.amount_paid)}`);
      onSaved(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (paymentId) => {
    setDeleting(paymentId);
    try {
      const { data } = await axios.delete(`${API_URL}/invoices/${invoice.id}/payments/${paymentId}`);
      toast.success("Payment removed");
      onSaved(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove payment");
    } finally {
      setDeleting("");
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Record Payment — {invoice.invoice_number}</DialogTitle>
          <DialogDescription>{invoice.client_name}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 text-center">
          {[["Invoice Total", total, "text-foreground", "payment-total"], ["Paid", paid, "text-green-400", "payment-paid"], ["Balance Due", balance, balance > 0 ? "text-red-400" : "text-green-400", "payment-balance"]].map(([label, val, cls, tid]) => (
            <div key={label} className="rounded-lg bg-secondary/40 border border-border p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
              <div className={`font-mono text-lg font-bold ${cls}`} data-testid={tid}>{fmt(val)}</div>
            </div>
          ))}
        </div>

        {canPay && (
          <form onSubmit={submit} className="space-y-4 border-t border-border pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Amount (₹)</Label>
                <Input type="number" step="0.01" min="0" max={balance} value={form.amount} onChange={(e) => set({ amount: e.target.value })}
                  className="bg-secondary/50 border-border font-mono" required data-testid="payment-amount-input" />
                <button type="button" className="text-xs text-primary hover:underline" onClick={() => set({ amount: Number(balance.toFixed(2)) })} data-testid="payment-full-btn">Pay full balance</button>
              </div>
              <div className="space-y-1">
                <Label>Payment Date</Label>
                <Input type="date" value={form.payment_date} onChange={(e) => set({ payment_date: e.target.value })} className="bg-secondary/50 border-border" required data-testid="payment-date-input" />
              </div>
              <div className="space-y-1">
                <Label>Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => set({ payment_mode: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer / NEFT / RTGS</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>UTR / Reference No.</Label>
                <Input value={form.utr_number} onChange={(e) => set({ utr_number: e.target.value })} className="bg-secondary/50 border-border font-mono" placeholder="e.g. HDFCN52026091200123" data-testid="payment-utr-input" />
              </div>
              <div className="space-y-1">
                <Label>Deposit Bank Account</Label>
                <Select value={form.bank_account_id} onValueChange={(v) => set({ bank_account_id: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="payment-bank-select"><SelectValue placeholder="Select (optional)" /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} — {b.account_number?.slice(-4)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>TDS Deducted by Client</Label>
                <div className="flex items-center gap-3 h-10">
                  <input type="checkbox" checked={form.tds_applicable} onChange={(e) => set({ tds_applicable: e.target.checked })} className="rounded" data-testid="payment-tds-checkbox" />
                  <Select value={String(form.tds_rate)} onValueChange={(v) => set({ tds_rate: v })} disabled={!form.tds_applicable}>
                    <SelectTrigger className="bg-secondary/50 border-border w-28" data-testid="payment-tds-rate-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="1">1%</SelectItem><SelectItem value="2">2%</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} className="bg-secondary/50 border-border" data-testid="payment-notes-input" />
            </div>
            <p className="text-xs text-muted-foreground">Payment is also posted to the client ledger{form.bank_account_id && form.utr_number ? " and recorded as a bank receipt" : ""}.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Close</Button>
              <Button type="submit" className="glow-primary bg-primary" disabled={saving} data-testid="payment-submit-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record Payment"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {invoice.payments?.length > 0 && (
          <div className="border-t border-border pt-4 space-y-2">
            <Label className="text-primary font-semibold">Payment History</Label>
            {invoice.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 text-sm rounded-lg bg-secondary/30 border border-border px-3 py-2" data-testid={`payment-row-${p.id}`}>
                <div className="min-w-0">
                  <span className="font-mono text-foreground font-semibold">{fmt(p.amount)}</span>
                  <span className="text-muted-foreground ml-2">{p.payment_date} · {p.payment_mode?.replace("_", " ")}</span>
                  {p.utr_number && <span className="text-muted-foreground ml-2 font-mono">UTR {p.utr_number}</span>}
                  {p.tds_applicable && <span className="text-amber-400 ml-2">TDS {p.tds_rate}%</span>}
                </div>
                <Button variant="ghost" size="icon" className="text-red-400 shrink-0" onClick={() => remove(p.id)} disabled={!!deleting} data-testid={`payment-delete-${p.id}`} title="Remove payment">
                  {deleting === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
